/**
 * BookingManagement Routes
 *
 * Main router for the Booking Management domain.
 * Uses lazy-loading for all sub-pages except the dashboard (eager).
 *
 * Route structure:
 *  /                      -> Dashboard (user-facing landing for ALL users)
 *  /admin/overview        -> Admin Overview (admin/BookingAdmin landing)
 *  /my-bookings           -> My Bookings
 *  /my-licences           -> Driver licence (user)
 *  /all                   -> All Bookings (planning view)
 *  /calendar              -> Calendar view
 *  /create                -> New booking form
 *  /view/:id              -> Booking detail
 *  /log/:type/:bookingId  -> Pre/post trip log form
 *  /resources             -> Resource management (admin)
 *  /lookups/*             -> Lookup tables management (admin)
 *  /settings              -> Booking settings (admin)
 *  /reports               -> Booking reports (admin)
 */

import { Routes, Route, Navigate } from 'react-router-dom';
import { lazy, Suspense } from 'react';
import MainLayout from '../../../layouts/MainLayout';
import { Loader, Center } from '@/components/ui';
import { ProtectedRoute } from '@/components/ccms-components/auth/components/ProtectedRoute';

// Eager-load the dashboard for instant access
import BookingDashboard from './dashboard';

// Lazy-load remaining pages for performance
const MyBookings = lazy(() => import('./my-bookings'));
const AllBookings = lazy(() => import('./all'));
const BookingCalendar = lazy(() => import('./calendar'));
const CreateBooking = lazy(() => import('./create'));
const BookingDetail = lazy(() => import('./view'));
const PrePostLog = lazy(() => import('./log'));
const ResourceManagement = lazy(() => import('./admin/resources'));
const ResourceDetail = lazy(() => import('./admin/resources/[id]'));
const ResourceCreate = lazy(() => import('./admin/resources/create'));
const BookingSettings = lazy(() => import('./admin/settings'));
const BookingReports = lazy(() => import('./admin/reports'));
const LookupManagement = lazy(() => import('./admin/lookups'));
const DriverProfilesAdmin = lazy(() => import('./admin/driver-profiles'));
const InventoryAuditAdmin = lazy(() => import('./admin/inventory-audit'));
const AdminOverview        = lazy(() => import('./admin/overview'));
const RfidEnrolmentAdmin   = lazy(() => import('./admin/rfid-enrolment'));
const TrackedAssetsAdmin   = lazy(() => import('./admin/tracked-assets'));
const ExpiryCalendarAdmin  = lazy(() => import('./admin/expiry-calendar'));
const MyLicences           = lazy(() => import('./my-licences'));

// Roles allowed to reach any admin-gated booking route. Mirrors the
// sidebar's `BOOKING_ADMIN_ROLES` so menu visibility and route protection
// stay in lockstep.
const BOOKING_ADMIN_ROLES = ['BookingAdmin', 'Admin'];

// Loading fallback while lazy chunks are fetched
const LoadingFallback = () => (
  <Center h={400}>
    <Loader size="lg" />
  </Center>
);

/**
 * BookingManagementRoutes
 *
 * Wraps all booking routes with the shared MainLayout
 * (sidebar, topbar, breadcrumbs, etc.) scoped to the
 * "bookings" domain key.
 */
export default function BookingManagementRoutes() {
  return (
    <MainLayout domainKey="bookings">
      <Suspense fallback={<LoadingFallback />}>
        <Routes>
          {/* Dashboard – default route (admin landing) */}
          <Route path="/" element={<BookingDashboard />} />

          {/* My Bookings – staff landing page */}
          <Route path="/my-bookings" element={<MyBookings />} />

          {/* All Bookings — planning view (BookingAdmin / Admin) */}
          <Route path="/all" element={<ProtectedRoute requiredRoles={BOOKING_ADMIN_ROLES}><AllBookings /></ProtectedRoute>} />

          {/* Calendar view */}
          <Route path="/calendar" element={<BookingCalendar />} />

          {/* Create new booking */}
          <Route path="/create" element={<CreateBooking />} />

          {/* Booking detail */}
          <Route path="/view/:id" element={<BookingDetail />} />

          {/* Pre / Post trip log form */}
          <Route path="/log/:type/:bookingId" element={<PrePostLog />} />

          {/* Resource management (BookingAdmin / Admin) */}
          <Route path="/resources" element={<ProtectedRoute requiredRoles={BOOKING_ADMIN_ROLES}><ResourceManagement /></ProtectedRoute>} />
          <Route path="/resources/create" element={<ProtectedRoute requiredRoles={BOOKING_ADMIN_ROLES}><Suspense fallback={<LoadingFallback />}><ResourceCreate /></Suspense></ProtectedRoute>} />
          <Route path="/resources/:id" element={<ProtectedRoute requiredRoles={BOOKING_ADMIN_ROLES}><Suspense fallback={<LoadingFallback />}><ResourceDetail /></Suspense></ProtectedRoute>} />

          {/* Settings (BookingAdmin / Admin) */}
          <Route path="/settings" element={<ProtectedRoute requiredRoles={BOOKING_ADMIN_ROLES}><BookingSettings /></ProtectedRoute>} />

          {/* Reports (BookingAdmin / Admin) */}
          <Route path="/reports" element={<ProtectedRoute requiredRoles={BOOKING_ADMIN_ROLES}><BookingReports /></ProtectedRoute>} />

          {/* Lookups (BookingAdmin / Admin) */}
          <Route path="/lookups/*" element={<ProtectedRoute requiredRoles={BOOKING_ADMIN_ROLES}><Suspense fallback={<LoadingFallback />}><LookupManagement /></Suspense></ProtectedRoute>} />

          {/* Driver profiles (admin) — Phase 5a */}
          <Route path="/admin/driver-profiles" element={<ProtectedRoute requiredRoles={BOOKING_ADMIN_ROLES}><Suspense fallback={<LoadingFallback />}><DriverProfilesAdmin /></Suspense></ProtectedRoute>} />

          {/* Inventory audit (admin) — RFID Phase 3 */}
          <Route path="/admin/inventory-audit" element={<ProtectedRoute requiredRoles={BOOKING_ADMIN_ROLES}><Suspense fallback={<LoadingFallback />}><InventoryAuditAdmin /></Suspense></ProtectedRoute>} />

          {/* Admin Overview (admin dashboard) */}
          <Route path="/admin/overview" element={<ProtectedRoute requiredRoles={BOOKING_ADMIN_ROLES}><Suspense fallback={<LoadingFallback />}><AdminOverview /></Suspense></ProtectedRoute>} />

          {/* RFID enrolment hub */}
          <Route path="/admin/rfid-enrolment" element={<ProtectedRoute requiredRoles={BOOKING_ADMIN_ROLES}><Suspense fallback={<LoadingFallback />}><RfidEnrolmentAdmin /></Suspense></ProtectedRoute>} />

          {/* Tracked assets live view */}
          <Route path="/admin/tracked-assets" element={<ProtectedRoute requiredRoles={BOOKING_ADMIN_ROLES}><Suspense fallback={<LoadingFallback />}><TrackedAssetsAdmin /></Suspense></ProtectedRoute>} />

          {/* Expiry calendar */}
          <Route path="/admin/expiry-calendar" element={<ProtectedRoute requiredRoles={BOOKING_ADMIN_ROLES}><Suspense fallback={<LoadingFallback />}><ExpiryCalendarAdmin /></Suspense></ProtectedRoute>} />

          {/* My licences (user) */}
          <Route path="/my-licences" element={<MyLicences />} />

          {/* Fallback – redirect unknown paths to dashboard */}
          <Route path="*" element={<Navigate to="/app/bookings" replace />} />
        </Routes>
      </Suspense>
    </MainLayout>
  );
}
